import { useSyncExternalStore } from 'react';
import { jobEventSchema, type Job } from '@xphoto/shared';
import { fetchJobs, jobStreamUrl } from './jobs-api';
import { ApiError } from '../gallery/api';
import {
  computeActivity,
  dismissedIds,
  jobsById,
  mergeJobs,
  mergeList,
  pruneDismissed,
  readDismissed,
  writeDismissed,
  type DismissedMap,
  type JobActivity,
} from './job-activity';

/**
 * Cloche de notifications : SEED (`GET /api/jobs`) + flux SSE (`GET /api/jobs/stream`),
 * fusionnés par `id`, dérivés en listes affichables via la logique PURE `job-activity.ts`.
 *
 * ⚠ SOURCE PARTAGÉE (perf) : l'activité d'une bibliothèque vit dans UN store
 * module-level (`JobActivitySource`) partagé par TOUS les appelants du hook via
 * `useSyncExternalStore`. Avant, chaque `useJobActivity(libraryId)` ouvrait sa PROPRE
 * connexion SSE + son PROPRE SEED + son PROPRE polling 5 s : sur un écran, la cloche
 * (`NotificationBell`, dans `AppChrome`) ET la galerie (`GalleryView`, qui n'a besoin
 * que d'un booléen « un scan tourne ») ET `useRefreshAfterJob` (pages dédup/EXIF)
 * dupliquaient 2–3× le MÊME flux. Désormais : UNE connexion SSE + UN SEED + UN poll
 * par bibliothèque, quel que soit le nombre de consommateurs. La signature du hook est
 * INCHANGÉE → aucun site d'appel ni test à modifier.
 *
 * Points clés (préservés) :
 * - Le slot actif passe en `?slot=N` (EventSource ne pose pas d'en-tête) — géré par
 *   `jobStreamUrl`.
 * - `EventSource` est FEATURE-DÉTECTÉ : absent (happy-dom / tests), le flux est un
 *   no-op → le SEED (fetch) fonctionne seul.
 * - Polling de REPLI actif SEULEMENT tant qu'un job tourne (le SSE peut être bufferisé
 *   par un proxy) ; résultats FUSIONNÉS (jamais un remplacement brutal).
 * - « Dismiss » persisté en `localStorage` AVEC horodatage : survit au F5, purgé par
 *   l'ÂGE (24 h) seulement (cf. `job-activity.ts`).
 * - Horloge de purge 24 h (60 s) → les terminés > 24 h disparaissent sans nouvel
 *   événement.
 */

/** Nature d'erreur du SEED (pour un message éventuel). */
export type SeedStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseJobActivity extends JobActivity {
  /** État de chargement du SEED initial. */
  status: SeedStatus;
  /**
   * Le flux SSE est TOMBÉ définitivement (`CLOSED`) et n'est pas encore rétabli :
   * une reconnexion est planifiée et le polling de repli prend le relais, MÊME sans
   * job actif connu. Exposé pour les tests et un éventuel indicateur d'UI.
   */
  streamDegraded: boolean;
  /** Masque un job terminé (persisté). No-op sur un job actif. */
  dismiss: (id: string) => void;
  /** Masque tous les jobs terminés actuellement visibles. */
  dismissAll: () => void;
  /** Recharge le SEED (bouton « Réessayer »). */
  reload: () => void;
}

/** Instantané immuable exposé par le store (identité stable entre deux changements). */
interface ActivitySnapshot extends JobActivity {
  status: SeedStatus;
  streamDegraded: boolean;
}

type Listener = () => void;

const SEED_LIMIT = 50;
const POLL_INTERVAL_MS = 5000;
const CLOCK_INTERVAL_MS = 60_000;

/**
 * RECONNEXION DU FLUX SSE (audit F4) — back-off exponentiel PLAFONNÉ.
 *
 * `EventSource` ne se reconnecte de lui-même que sur une coupure de TRANSPORT. Sur une
 * réponse HTTP **non-2xx** (429 du limiteur, 502 du proxy, session expirée) il passe en
 * `CLOSED` **définitif** : sans reprise explicite, la cloche devient silencieuse SANS
 * AUCUN SIGNAL, et un job démarré ensuite n'apparaît jamais (le polling de repli n'était
 * armé que si un job actif était DÉJÀ connu).
 *
 * Le plafond de 60 s est essentiel : c'est souvent un **429** qui a tué le flux, et
 * marteler le serveur le maintiendrait limité. Un facteur 2 depuis 2 s atteint le
 * plafond en 5 tentatives (2, 4, 8, 16, 32, puis 60 s), soit au pire 1 requête/minute.
 */
const SSE_RECONNECT_BASE_MS = 2000;
const SSE_RECONNECT_MAX_MS = 60_000;

/**
 * Polling de repli en mode DÉGRADÉ (flux mort, aucun job actif connu) : nettement plus
 * lent que le polling « un job tourne » (5 s). On ne cherche ici qu'à DÉTECTER qu'un
 * job a démarré — pas à suivre sa progression seconde par seconde — sans ajouter de
 * pression sur un serveur qui vient peut-être de nous limiter.
 */
const DEGRADED_POLL_INTERVAL_MS = 20_000;

/**
 * Délai avant la tentative de reconnexion n° `attempt` (1-based), plafonné.
 * Pur (testable) : `2 s, 4 s, 8 s, 16 s, 32 s, 60 s, 60 s…`.
 */
export function sseReconnectDelay(attempt: number): number {
  const raw = SSE_RECONNECT_BASE_MS * 2 ** Math.max(0, attempt - 1);
  return Math.min(SSE_RECONNECT_MAX_MS, raw);
}

/**
 * Store d'activité des jobs pour UNE bibliothèque, partagé par tous les abonnés.
 *
 * Cycle de vie par comptage de références : le premier abonné démarre le SEED + le SSE
 * + l'horloge ; le dernier départ arrête tout et REMET À ZÉRO l'état transitoire (mais
 * la source reste dans le registre → identité de `subscribe` stable, pas d'orphelin en
 * StrictMode qui double-abonne). Un ré-abonnement ré-amorce un SEED frais.
 */
class JobActivitySource {
  private jobs: ReadonlyMap<string, Job> = new Map();
  private dismissed: DismissedMap = readDismissed();
  private status: SeedStatus = 'idle';
  private now = Date.now();
  private snapshot: ActivitySnapshot;

  private readonly listeners = new Set<Listener>();
  private refCount = 0;
  private seedController: AbortController | null = null;
  private eventSource: EventSource | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollIntervalMs: number | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  /** Reconnexion SSE planifiée (back-off), ou `null`. */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Nombre de fermetures définitives consécutives (pilote le back-off). */
  private sseAttempts = 0;
  /** Flux SSE tombé et pas encore rétabli → polling forcé. */
  private streamDegraded = false;

  constructor(private readonly libraryId: string) {
    this.snapshot = this.buildSnapshot();
  }

  private buildSnapshot(): ActivitySnapshot {
    const activity = computeActivity(this.jobs, dismissedIds(this.dismissed), this.now);
    return { ...activity, status: this.status, streamDegraded: this.streamDegraded };
  }

  /** Recalcule l'instantané, synchronise le polling, notifie les abonnés. */
  private update(): void {
    this.snapshot = this.buildSnapshot();
    this.syncPoll();
    for (const listener of this.listeners) listener();
  }

  getSnapshot = (): ActivitySnapshot => this.snapshot;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    this.refCount += 1;
    if (this.refCount === 1) this.start();
    return () => {
      this.listeners.delete(listener);
      this.refCount -= 1;
      if (this.refCount === 0) this.stop();
    };
  };

  private start(): void {
    if (!this.libraryId) {
      this.status = 'idle';
      this.update();
      return;
    }
    this.runSeed();
    this.openStream();
    this.clockTimer = setInterval(() => {
      this.now = Date.now();
      const pruned = pruneDismissed(this.dismissed, this.now);
      if (pruned.size !== this.dismissed.size) {
        this.dismissed = pruned;
        writeDismissed(pruned);
      }
      this.update();
    }, CLOCK_INTERVAL_MS);
  }

  /**
   * Ouvre (ou ré-ouvre) le flux SSE. `EventSource` est FEATURE-DÉTECTÉ : absent
   * (happy-dom / tests), le flux est un no-op → le SEED (fetch) fonctionne seul, et
   * on ne se déclare PAS dégradé (rien n'est tombé, il n'y a simplement pas de flux).
   */
  private openStream(): void {
    if (typeof EventSource === 'undefined') return;
    this.closeStream();
    const es = new EventSource(jobStreamUrl(this.libraryId), { withCredentials: true });
    es.addEventListener('progress', this.onSseEvent);
    es.addEventListener('finished', this.onSseEvent);
    es.onopen = this.onSseOpen;
    es.onerror = this.onSseError;
    this.eventSource = es;
  }

  /** Détache les écouteurs et ferme le flux courant (idempotent). */
  private closeStream(): void {
    const es = this.eventSource;
    if (!es) return;
    es.removeEventListener('progress', this.onSseEvent);
    es.removeEventListener('finished', this.onSseEvent);
    es.onopen = null;
    es.onerror = null;
    es.close();
    this.eventSource = null;
  }

  /** Flux rétabli : back-off soldé, mode dégradé levé (le polling lent s'arrête). */
  private onSseOpen = (): void => {
    this.sseAttempts = 0;
    if (!this.streamDegraded) return;
    this.streamDegraded = false;
    this.update();
  };

  /**
   * Erreur du flux. `EventSource` gère SEUL les coupures de transport (il repasse en
   * `CONNECTING` et réessaie) : on ne s'en mêle pas. Seule une fermeture DÉFINITIVE
   * (`CLOSED`, typiquement une réponse non-2xx) exige une reprise explicite.
   */
  private onSseError = (): void => {
    const es = this.eventSource;
    if (!es || es.readyState !== EventSource.CLOSED) return;
    this.closeStream();
    this.sseAttempts += 1;
    // Polling forcé DÈS MAINTENANT : sans ça, un job démarré pendant la coupure
    // n'apparaîtrait jamais (le repli n'était armé que sur un job déjà connu actif).
    if (!this.streamDegraded) {
      this.streamDegraded = true;
      this.update();
    }
    this.scheduleReconnect();
  };

  /** Planifie UNE reconnexion (jamais deux en parallèle) au délai de back-off courant. */
  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // Plus aucun abonné entre-temps : ne pas ressusciter un flux orphelin.
      if (this.refCount === 0) return;
      this.openStream();
    }, sseReconnectDelay(this.sseAttempts));
  }

  private stop(): void {
    this.seedController?.abort();
    this.seedController = null;
    this.closeStream();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.pollIntervalMs = null;
    }
    if (this.clockTimer !== null) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }
    // Remise à zéro de l'état transitoire → un futur abonné ré-amorce un SEED frais
    // (évite un état périmé entre deux montages, notamment entre tests).
    this.jobs = new Map();
    this.status = 'idle';
    this.sseAttempts = 0;
    this.streamDegraded = false;
    this.snapshot = this.buildSnapshot();
  }

  private runSeed(): void {
    this.seedController?.abort();
    const controller = new AbortController();
    this.seedController = controller;
    this.status = 'loading';
    this.update();
    fetchJobs(this.libraryId, SEED_LIMIT, controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        this.jobs = jobsById(list);
        this.status = 'ready';
        this.update();
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        // 401 = slot sans session : pas une vraie erreur d'activité, on reste vide.
        if (err instanceof ApiError && err.status === 401) {
          this.jobs = new Map();
          this.status = 'ready';
          this.update();
          return;
        }
        this.status = 'error';
        this.update();
      });
  }

  private onSseEvent = (event: MessageEvent): void => {
    try {
      const parsed = jobEventSchema.parse(JSON.parse(event.data as string));
      this.jobs = mergeJobs(this.jobs, parsed.job);
      this.update();
    } catch {
      // Événement invalide/illisible : ignoré sans casser le flux.
    }
  };

  /**
   * Démarre/arrête/re-cadence le polling de repli. Deux régimes :
   *  - un job TOURNE → 5 s (suivi fin de la progression, comme avant) ;
   *  - flux SSE DÉGRADÉ sans job actif connu → 20 s : on ne cherche qu'à détecter le
   *    DÉMARRAGE d'un job, sans marteler un serveur qui vient peut-être de nous limiter.
   * Sinon : aucun polling.
   */
  private syncPoll(): void {
    const wanted = !this.libraryId
      ? null
      : this.snapshot.activeCount > 0
        ? POLL_INTERVAL_MS
        : this.streamDegraded
          ? DEGRADED_POLL_INTERVAL_MS
          : null;
    if (wanted === this.pollIntervalMs) return; // déjà au bon régime (ou déjà arrêté)
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.pollIntervalMs = wanted;
    if (wanted === null) return;
    this.pollTimer = setInterval(() => {
      fetchJobs(this.libraryId, SEED_LIMIT)
        .then((list) => {
          this.jobs = mergeList(this.jobs, list);
          this.update();
        })
        .catch(() => {
          /* silencieux : le seed initial et le SSE couvrent les cas nominaux */
        });
    }, wanted);
  }

  reload = (): void => {
    if (this.libraryId) this.runSeed();
  };

  dismiss = (id: string): void => {
    const job = this.jobs.get(id);
    // Ne masque que des jobs terminaux (un job actif reste toujours visible).
    if (!job || job.status === 'queued' || job.status === 'running') return;
    if (this.dismissed.has(id)) return;
    const next = new Map(this.dismissed);
    next.set(id, Date.now());
    this.dismissed = next;
    writeDismissed(next);
    this.update();
  };

  dismissAll = (): void => {
    const at = Date.now();
    const next = new Map(this.dismissed);
    for (const job of this.jobs.values()) {
      if (job.status === 'done' || job.status === 'failed' || job.status === 'canceled') {
        if (!next.has(job.id)) next.set(job.id, at);
      }
    }
    if (next.size === this.dismissed.size) return;
    this.dismissed = next;
    writeDismissed(next);
    this.update();
  };

  /**
   * TEST-ONLY : coupe minuteries/connexions/SEED en vol (cf. `__resetJobActivityForTests`).
   * Réutilise `stop()` (idempotent, gardé) — jamais appelé en prod.
   */
  destroyForTests(): void {
    this.stop();
  }
}

/** Registre des sources par bibliothèque (jamais purgé → identité `subscribe` stable). */
const sources = new Map<string, JobActivitySource>();

function getSource(libraryId: string): JobActivitySource {
  let source = sources.get(libraryId);
  if (!source) {
    source = new JobActivitySource(libraryId);
    sources.set(libraryId, source);
  }
  return source;
}

/**
 * TEST-ONLY — vide le registre module-level partagé des sources d'activité.
 *
 * Une `JobActivitySource` VIT dans le registre partagé `sources` et CONSERVE son
 * état (notamment `dismissed`, lu de `localStorage` au montage) au-delà d'un
 * démontage : VOULU en prod (durabilité du « dismiss », cf. v0.25.0 — le store
 * reste monté dans `AppChrome`, et un éventuel remontage réutilise la même source,
 * cohérente avec `localStorage`). Or les modules sont isolés PAR FICHIER, pas par
 * `it` : dans une même suite, cet état survit entre deux tests → dépendance à
 * l'ordre (un job « dismissé » par un test masque le même job dans un test suivant,
 * même après `localStorage.clear()` qui n'atteint pas la copie en mémoire).
 *
 * Les suites appellent ceci en `beforeEach` pour repartir d'un store vierge (chaque
 * source relit alors un `localStorage` propre à son (re)montage). JAMAIS appelé en
 * prod → AUCUN impact sur le comportement applicatif.
 */
export function __resetJobActivityForTests(): void {
  for (const source of sources.values()) source.destroyForTests();
  sources.clear();
}

export function useJobActivity(libraryId: string): UseJobActivity {
  const source = getSource(libraryId);
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  return {
    ...snapshot,
    dismiss: source.dismiss,
    dismissAll: source.dismissAll,
    reload: source.reload,
  };
}
